import { CoordinatorService } from './coordinator.service';
import { AttachmentStorage, AttachmentRecord } from './attachment-storage';

/**
 * Migration result for a single attachment
 */
interface MigrationResult {
    cachename: string;
    success: boolean;
    error?: string;
    isExisting?: boolean;
}

/**
 * AttachmentMigration - Handles migration from OPFS to IndexedDB
 * 
 * This class migrates attachments from the old OPFS-based storage
 * (_attachments folder) to the new IndexedDB-based storage system.
 * 
 * The migration:
 * - Scans the OPFS _attachments directory
 * - For each file, checks if it exists in IndexedDB for the given project
 * - If not, stores the blob in IndexedDB with the existing cachename (MD5 hash)
 * - Associates the attachment with the specified project
 */
export class AttachmentMigration {
    private _migrationRan = new Set<string>(); // Track which projects have been migrated

    constructor(
        private coords: CoordinatorService,
        private storage: AttachmentStorage
    ) {}

    /**
     * Run migration for a project
     * 
     * @param projectId - The project to migrate attachments for
     * @param attachmentLs - List of attachment metadata from the project (to get filenames/mimetypes)
     */
    async migrateProject(
        projectId: string,
        attachmentLs: Array<{ cachename?: string; filename?: string; mimetype?: string; size?: number }>
    ): Promise<{ migrated: number; skipped: number; failed: number }> {
        // Don't run migration twice for the same project
        if (this._migrationRan.has(projectId)) {
            console.log('Migration already ran for project:', projectId);
            return { migrated: 0, skipped: 0, failed: 0 };
        }

        console.log('%c Starting OPFS to IndexedDB migration for project:', 'background: purple; color: white;', projectId);

        const results: MigrationResult[] = [];

        try {
            const r = await navigator.storage.getDirectory();
            let attachmentDir: FileSystemDirectoryHandle;
            
            try {
                attachmentDir = await r.getDirectoryHandle('_attachments');
            } catch (e) {
                // No _attachments directory exists - nothing to migrate
                console.log('No OPFS _attachments directory found, nothing to migrate');
                this._migrationRan.add(projectId);
                return { migrated: 0, skipped: 0, failed: 0 };
            }

            // Build a map of cachename -> metadata from the attachment list
            const metadataMap = new Map<string, { filename?: string; mimetype?: string; size?: number }>();
            for (const att of attachmentLs) {
                if (att.cachename) {
                    metadataMap.set(att.cachename, {
                        filename: att.filename,
                        mimetype: att.mimetype,
                        size: att.size
                    });
                }
            }

            // Only migrate files that are referenced by this project's attachments
            const cachenamesToMigrate = Array.from(metadataMap.keys());
            
            if (cachenamesToMigrate.length === 0) {
                console.log('No attachments to migrate for project:', projectId);
                this._migrationRan.add(projectId);
                return { migrated: 0, skipped: 0, failed: 0 };
            }

            // Process each attachment that belongs to this project
            for (const cachename of cachenamesToMigrate) {
                const result = await this._migrateAttachment(
                    projectId,
                    attachmentDir,
                    cachename,
                    metadataMap.get(cachename)
                );
                results.push(result);
            }

        } catch (e) {
            console.error('Migration failed:', e);
        }

        this._migrationRan.add(projectId);

        const migrated = results.filter(r => r.success && !r.isExisting).length;
        const skipped = results.filter(r => r.isExisting).length;
        const failed = results.filter(r => !r.success).length;

        console.log('%c Migration complete:', 'background: purple; color: white;', {
            projectId,
            migrated,
            skipped,
            failed
        });

        return { migrated, skipped, failed };
    }

    /**
     * Migrate a single attachment from OPFS to IndexedDB
     */
    private async _migrateAttachment(
        projectId: string,
        attachmentDir: FileSystemDirectoryHandle,
        cachename: string,
        metadata?: { filename?: string; mimetype?: string; size?: number }
    ): Promise<MigrationResult> {
        try {
            // Check if already exists in IndexedDB
            const existing = await this.storage.get(cachename, projectId);
            if (existing) {
                return { cachename, success: true, isExisting: true };
            }

            // Try to get the file from OPFS
            let fileHandle: FileSystemFileHandle;
            try {
                fileHandle = await attachmentDir.getFileHandle(cachename);
            } catch (e) {
                // File doesn't exist in OPFS - might be a URL-only attachment
                return { 
                    cachename, 
                    success: true, 
                    isExisting: true,
                    error: 'File not found in OPFS (may be URL-only attachment)'
                };
            }

            const file = await fileHandle.getFile();
            
            // Determine mimetype
            const mimetype = metadata?.mimetype || 
                this.coords.utils$.guessMimetype(metadata?.filename || cachename) ||
                'application/octet-stream';

            const isCloud = this.coords.project$.current?.isCloud || false;

            // Store in IndexedDB
            // Note: We use the existing cachename (MD5 hash) as the ID
            // This maintains backward compatibility
            const record: AttachmentRecord = {
                id: cachename,
                projectId,
                blob: new Blob([await file.arrayBuffer()], { type: mimetype }),
                md5Hash: cachename,
                filename: metadata?.filename || cachename,
                mimetype,
                size: file.size,
                createdAt: Date.now(),
                syncStatus: isCloud ? 'synced' : 'synced', // Assume already synced if it was in OPFS for cloud
                syncFailureCount: 0
            };

            // Store directly using internal method
            await this._storeRecord(record);

            console.log('Migrated attachment:', cachename);
            return { cachename, success: true };

        } catch (e: any) {
            console.error('Failed to migrate attachment:', cachename, e);
            return { cachename, success: false, error: e.message };
        }
    }

    /**
     * Store a record directly in IndexedDB (bypassing the store() method which recalculates hash)
     */
    private async _storeRecord(record: AttachmentRecord): Promise<void> {
        // Wait for storage to be ready
        if (!this.storage.isReady) {
            await new Promise<void>((resolve) => {
                const sub = this.storage.isReady$.subscribe((ready) => {
                    if (ready) {
                        sub.unsubscribe();
                        resolve();
                    }
                });
            });
        }

        // Use a direct IndexedDB transaction
        const db = await this._getDatabase();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(['attachments'], 'readwrite');
            const store = transaction.objectStore('attachments');
            const request = store.put(record);

            request.onsuccess = () => resolve();
            request.onerror = () => reject(new Error('Failed to store migrated attachment'));
        });
    }

    /**
     * Get the IndexedDB database instance
     */
    private async _getDatabase(): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open('referentka-attachments', 1);
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(new Error('Failed to open IndexedDB'));
        });
    }

    /**
     * Check if migration has run for a project
     */
    hasMigrated(projectId: string): boolean {
        return this._migrationRan.has(projectId);
    }

    /**
     * Force re-run migration for a project (for debugging)
     */
    resetMigration(projectId: string): void {
        this._migrationRan.delete(projectId);
    }

    /**
     * Clean up orphaned files in OPFS that are not referenced by any project
     * This should be called during maintenance, not during normal operation
     */
    async cleanupOrphanedOPFSFiles(): Promise<{ deleted: number; errors: number }> {
        let deleted = 0;
        let errors = 0;

        try {
            const r = await navigator.storage.getDirectory();
            let attachmentDir: FileSystemDirectoryHandle;
            
            try {
                attachmentDir = await r.getDirectoryHandle('_attachments');
            } catch (e) {
                return { deleted: 0, errors: 0 };
            }

            // Get all files in OPFS
            const opfsFiles: string[] = [];
            for await (const entry of (attachmentDir as any).values()) {
                if (entry.kind === 'file') {
                    opfsFiles.push(entry.name);
                }
            }

            // For each OPFS file, check if it exists in IndexedDB
            for (const filename of opfsFiles) {
                try {
                    const record = await this.storage.getById(filename);
                    if (record) {
                        // File is in IndexedDB, safe to delete from OPFS
                        await attachmentDir.removeEntry(filename);
                        deleted++;
                        console.log('Deleted orphaned OPFS file:', filename);
                    }
                } catch (e) {
                    errors++;
                    console.error('Error cleaning up OPFS file:', filename, e);
                }
            }

        } catch (e) {
            console.error('Cleanup failed:', e);
        }

        console.log('OPFS cleanup complete:', { deleted, errors });
        return { deleted, errors };
    }
}

